import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { SitemapNavigation } from './SitemapNavigation';
import { SitemapAlphaNavigation } from './SitemapAlphaNavigation';
import { SitemapIndexesNavigation } from './SitemapIndexesNavigation';

import {
    ALPHA_TITLE_ID,
    URI_SEGMENT_DEALER,
    URI_SEGMENT_LOCATION,
    LINK_CREATORS,
    LINK_ITEMS,
    PARAM_LOCATION,
} from '../constants/sitemapConstants';
import { getNavLinks } from '../sitemapHelpers';

import styles from '../styles/sitemap.scss';

function displayItemsList(items) {
    return (
        <ul className="rowFlex cf" data-tn="sitemap-full-list">
            {items.map((item, i) => (
                <li className="colXs6 colLg4" key={`${item.linkUrl}${i}`}>
                    <Link
                        href={item.linkUrl}
                        className={styles.link}
                        dataTn={`sitemap-list-link-${item.label}`}
                        data-link
                    >
                        {item.title}
                    </Link>
                </li>
            ))}
        </ul>
    );
}

function showResultsCounter(sitemapLinks) {
    const { items, resultsHeader } = sitemapLinks;
    return (
        <h2 className={styles.sitemapResultsHeader} data-tn="sitemap-results-header">
            <FormattedMessage
                id="sitemap.countingResults"
                defaultMessage="Showing {count, plural, one {# result} other {# results}} for {resultsHeader}"
                values={{
                    count: items.length,
                    resultsHeader,
                }}
            />
        </h2>
    );
}

const constructTitle = (filterType, sitemapLinks, vertical) => {
    const staticLinks = getNavLinks(vertical);
    const staticLink =
        filterType === PARAM_LOCATION
            ? staticLinks.find(({ label }) => label === URI_SEGMENT_LOCATION)
            : staticLinks.find(({ label }) => label === filterType);
    const headerText = sitemapLinks.headerText;
    const title = staticLink?.title || '';
    const resultsHeader = sitemapLinks.resultsHeader || '';
    if (filterType === LINK_CREATORS || filterType === LINK_ITEMS) {
        const items = sitemapLinks.items || [];
        const first = items[0]?.title || '';
        const last = items[items.length - 1]?.title || '';
        return (
            <FormattedMessage
                id="sitemap.creators.title"
                defaultMessage="{headerText} - {title} - {first} - {last}"
                values={{ first, headerText, last, title }}
            />
        );
    }
    return (
        <FormattedMessage
            id="sitemapValueType.title"
            defaultMessage="{hasResultsHeader, select,
                            true {{headerText} - {title} - {resultsHeader}}
                            other {{headerText} - {title}}
                        }"
            values={{ headerText, title, resultsHeader, hasResultsHeader: !!resultsHeader }}
        />
    );
};
export const SitemapValueTypeComponent = ({ sitemap, vertical, filterType, filterTypeValue }) => {
    const sitemapLinks = sitemap?.sitemapLinks?.[0] || {};
    const hasChildren = !!sitemapLinks.items?.[0]?.items;
    const isDealersPage = sitemapLinks.linksType === URI_SEGMENT_DEALER;

    return (
        <div className={styles.sitemapWrapper}>
            <h1 className={styles.sitemapMainHeader}>
                {constructTitle(filterType, sitemapLinks, vertical)}
            </h1>
            <SitemapNavigation vertical={vertical} filterType={filterType} />
            {hasChildren ? (
                <>
                    <SitemapAlphaNavigation items={sitemapLinks.items} />
                    {sitemapLinks.items.map(item => (
                        <Fragment key={`${ALPHA_TITLE_ID}${item.title}`}>
                            <h2 id={`${ALPHA_TITLE_ID}${item.title}`} data-grouped-header>
                                {item.title.toUpperCase()}
                            </h2>
                            {displayItemsList(item.items)}
                        </Fragment>
                    ))}
                </>
            ) : (
                <>
                    {isDealersPage && (
                        <SitemapIndexesNavigation vertical={vertical} filterType={filterType} />
                    )}
                    {!isDealersPage && filterTypeValue && showResultsCounter(sitemapLinks)}
                    {displayItemsList(sitemapLinks.items)}
                </>
            )}
        </div>
    );
};
/*


*/
SitemapValueTypeComponent.propTypes = {
    vertical: PropTypes.string.isRequired,
    filterType: PropTypes.string.isRequired,
    sitemap: PropTypes.object.isRequired,
    filterTypeValue: PropTypes.string,
    viewer: PropTypes.object.isRequired,
};

export const SitemapValueType = createFragmentContainer(SitemapValueTypeComponent, {
    sitemap: graphql`
        fragment SitemapValueType_sitemap on Sitemap {
            sitemapLinks {
                headerText
                linksType
                resultsHeader
                items {
                    title
                    label
                    linkUrl
                    items {
                        label
                        title
                        linkUrl
                    }
                }
            }
        }
    `,
});
